import React, { useLayoutEffect, useRef, useState } from 'react';
import ErrorMessage from '../../../ui_kit/utilities/error_message';
import QuestionWrapper from '../../../../wrappers/question_wrapper/index';

import styles from '../../questionnaire.module.css';

const sanitizeInput = (raw) => raw.replace(/[^\d/]/g, '').slice(0, 10);

// Returns { year, month, day } for known formats, or null.
// Accepts: Date instances, MM/DD/YYYY strings (API serializer output), and
// ISO 8601 YYYY-MM-DD strings. Never delegates to `new Date(string)` because
// slash-delimited strings are invalid per spec (Firefox/Safari return Invalid
// Date) and ISO strings are parsed as UTC midnight (shifts the day in
// negative-offset timezones).
const parseToYMD = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }

  const mmddyyyy = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyy) {
    return { year: Number(mmddyyyy[3]), month: Number(mmddyyyy[1]), day: Number(mmddyyyy[2]) };
  }

  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
  }

  return null;
};

const dateToInputValue = (value) => {
  const ymd = parseToYMD(value);
  if (!ymd) return '';
  const mm = String(ymd.month).padStart(2, '0');
  const dd = String(ymd.day).padStart(2, '0');
  return `${mm}/${dd}/${ymd.year}`;
};

// Only reformat when the user is appending at the end. Editing in the middle
// is left alone so the caret stays where the user put it.
const autoFormat = (prev, next, cursor) => {
  const isAppending = next.length > prev.length && cursor === next.length;
  if (!isAppending) return { value: next, cursor };

  const segments = next.split('/');

  if (segments.length === 1 && segments[0].length === 2) {
    const mm = parseInt(segments[0], 10);
    if (mm >= 1 && mm <= 12) {
      return { value: `${segments[0]}/`, cursor: cursor + 1 };
    }
    return { value: `${segments[0][0]}/${segments[0][1]}`, cursor: cursor + 1 };
  }

  if (segments.length === 2 && segments[1].length === 2) {
    const dd = parseInt(segments[1], 10);
    if (dd >= 1 && dd <= 31) {
      return { value: `${segments[0]}/${segments[1]}/`, cursor: cursor + 1 };
    }
    return { value: `${segments[0]}/${segments[1][0]}/${segments[1][1]}`, cursor: cursor + 1 };
  }

  return { value: next, cursor };
};

const validateDateOfBirth = (value) => {
  if (!value) {
    return { valid: false, error: 'Please enter your date of birth as MM/DD/YYYY' };
  }

  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    return { valid: false, error: 'Please enter your date of birth as MM/DD/YYYY' };
  }

  const [mm, dd, yyyy] = value.split('/').map(Number);

  if (mm < 1 || mm > 12) {
    return { valid: false, error: 'Month must be between 1 and 12' };
  }

  if (dd < 1 || dd > 31) {
    return { valid: false, error: 'Day must be between 1 and 31' };
  }

  // Round-trip catches impossible combinations like 02/30 or 04/31.
  const date = new Date(yyyy, mm - 1, dd);
  if (date.getMonth() !== mm - 1 || date.getDate() !== dd || date.getFullYear() !== yyyy) {
    return { valid: false, error: "That day doesn't exist for that month" };
  }

  if (yyyy < 1900) {
    return { valid: false, error: 'Year must be 1900 or later' };
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (date > startOfToday) {
    return { valid: false, error: "Date of birth can't be in the future" };
  }

  return { valid: true, date };
};

export default function Age({
  title = 'What is your age?',
  para = "We'll match you with someone who is in the same age range as you.",
  answers,
  onBack,
  onSubmit,
}) {
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);
  const [value, setValue] = useState(dateToInputValue(answers && answers.date_of_birth));
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    if (pendingCursorRef.current === null || !inputRef.current) return;
    const pos = pendingCursorRef.current;
    pendingCursorRef.current = null;
    try {
      inputRef.current.setSelectionRange(pos, pos);
    } catch (_) {
      // Some browsers/input types disallow setSelectionRange; safe to ignore.
    }
  });

  const handleChange = (e) => {
    const rawCursor = e.target.selectionStart;
    const sanitized = sanitizeInput(e.target.value);
    const safeCursor = Math.min(rawCursor !== null ? rawCursor : sanitized.length, sanitized.length);
    const { value: nextValue, cursor: nextCursor } = autoFormat(value, sanitized, safeCursor);
    pendingCursorRef.current = nextCursor;
    setValue(nextValue);
    if (error) setError('');
  };

  const handleBlur = () => {
    if (value.length === 0) return;
    const result = validateDateOfBirth(value);
    if (!result.valid) {
      setError(result.error);
      return;
    }
    setValue(dateToInputValue(result.date));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = validateDateOfBirth(value);
    if (!result.valid) {
      setError(result.error);
      return;
    }
    setError('');
    if (onSubmit) onSubmit({ date_of_birth: result.date });
  };

  return (
    <QuestionWrapper onSubmit={handleSubmit} onBack={onBack} isDisabled={false}>
      <h5 className={styles.question}>{title}</h5>
      <p className={styles.question_para}>{para}</p>
      <input
        ref={inputRef}
        className={`${styles.tile} ${styles.date_picker} ${styles.date_text_input}`}
        type='text'
        inputMode='numeric'
        placeholder='MM/DD/YYYY'
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={10}
      />
      {error && <ErrorMessage error={error} />}
    </QuestionWrapper>
  );
}
